import React, { useMemo, useState } from "react";
import { ActivityIndicator, FlatList, Image, Pressable, ScrollView, Text, TextInput, View } from "react-native";
import { useTranslation } from "react-i18next";
import { STICKERS, STICKER_BY_ID, STICKER_TABS, type StickerCategory } from "@/src/constants/stickers";

const TAB_LABEL: Record<StickerCategory, string> = {
  people: "LABEL_STICKER_TAB_PEOPLE",
  faces: "LABEL_STICKER_TAB_FACES",
  clothes: "LABEL_STICKER_TAB_CLOTHES",
  hair: "LABEL_STICKER_TAB_HAIR",
  bodies: "LABEL_STICKER_TAB_BODIES",
  accessories: "LABEL_STICKER_TAB_ACCESSORIES",
  emoji: "LABEL_STICKER_TAB_EMOJI",
};
import type { StickerSession } from "@/src/hooks/features/posts/use-sticker-session";

/** Compact sticker tray docked inline below the post while it is in sticker mode. */
const StickerTray = ({ session }: { session: StickerSession }) => {
  const { t } = useTranslation();
  const { draft, mine, dirty, pending, pick, rotateBy, scaleBy, save, remove, done, imageIndexes, mediaIndex, goTo } = session;
  const pos = imageIndexes.indexOf(mediaIndex);
  const [tab, setTab] = useState<StickerCategory>(() => STICKER_BY_ID[draft?.stickerId ?? ""]?.category ?? "people");
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const items = useMemo(
    () => (q ? STICKERS.filter((s) => s.label.toLowerCase().includes(q) || s.id.includes(q)) : STICKERS.filter((s) => s.category === tab)),
    [q, tab],
  );
  return (
    <View testID="sticker-tray" className="border-t border-ash-3 px-3 py-2 gap-2">
      <View className="flex-row items-center gap-1">
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 4 }} className="flex-1">
          {STICKER_TABS.map((c) => (
            <Pressable
              key={c}
              testID={`sticker-tab-${c}`}
              onPress={() => { setTab(c); setQuery(""); }}
              className={`px-2.5 py-1 rounded-full ${tab === c && !q ? "bg-primary-2" : "border border-ash-3"}`}
            >
              <Text className="text-xs text-onyx">{t(TAB_LABEL[c])}</Text>
            </Pressable>
          ))}
        </ScrollView>
        <TextInput
          testID="sticker-search"
          value={query}
          onChangeText={setQuery}
          placeholder={t("LABEL_STICKER_SEARCH")}
          className="w-28 px-2 py-1 rounded-lg border border-ash-3 text-xs text-onyx"
        />
      </View>
      {/* FlatList only mounts what is on screen, so sticker art is fetched on demand. */}
      <FlatList
        horizontal
        testID="sticker-palette"
        data={items}
        keyExtractor={(s) => s.id}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: 4 }}
        initialNumToRender={12}
        windowSize={3}
        ListEmptyComponent={<Text className="text-xs text-ash-2 py-3">{t("LABEL_STICKER_NO_RESULTS")}</Text>}
        renderItem={({ item: s }) => (
          <Pressable
            testID={`sticker-option-${s.id}`}
            onPress={() => pick(s.id)}
            accessibilityLabel={s.label}
            className={`p-1 rounded-lg ${draft?.stickerId === s.id ? "bg-primary-2" : "hover:bg-ash-3"}`}
          >
            <Image source={s.source} style={{ width: 44, height: 44 }} resizeMode="contain" />
          </Pressable>
        )}
      />
      <View className="flex-row flex-wrap items-center gap-2">
        {imageIndexes.length > 1 ? (
          <View className="flex-row items-center gap-1">
            <Btn label="‹" testID="sticker-prev-image" disabled={pos <= 0} onPress={() => goTo(imageIndexes[pos - 1])} />
            <Text className="text-xs text-ash-2">{pos + 1}/{imageIndexes.length}</Text>
            <Btn label="›" testID="sticker-next-image" disabled={pos >= imageIndexes.length - 1} onPress={() => goTo(imageIndexes[pos + 1])} />
          </View>
        ) : null}
        {draft ? (
          <>
            <Btn label="⟲" testID="sticker-rotate-left" a11y={`${t("LABEL_STICKER_ROTATE")} -`} onPress={() => rotateBy(-1)} />
            <Btn label="⟳" testID="sticker-rotate-right" a11y={`${t("LABEL_STICKER_ROTATE")} +`} onPress={() => rotateBy(1)} />
            <Btn label="−" testID="sticker-smaller" a11y={`${t("LABEL_STICKER_SIZE")} -`} onPress={() => scaleBy(-1)} />
            <Btn label="+" testID="sticker-bigger" a11y={`${t("LABEL_STICKER_SIZE")} +`} onPress={() => scaleBy(1)} />
          </>
        ) : (
          <Text className="text-xs text-ash-2 flex-shrink">{t("LABEL_STICKER_PICK_HINT")}</Text>
        )}
        <View className="flex-1" />
        {pending ? <ActivityIndicator size="small" testID="sticker-pending" /> : null}
        {mine ? (
          <Pressable testID="sticker-remove-mine" disabled={!!pending} onPress={() => remove()} className="px-3 py-1.5 rounded-lg border border-ash-3">
            <Text className="text-xs text-onyx">{t("LABEL_STICKER_REMOVE_MINE")}</Text>
          </Pressable>
        ) : null}
        {dirty ? (
          <Pressable testID="sticker-place" disabled={!!pending} onPress={save} className="px-3 py-1.5 rounded-lg bg-primary">
            <Text className="text-xs text-chalk font-semibold">{t(mine ? "LABEL_STICKER_MOVE" : "LABEL_STICKER_PLACE")}</Text>
          </Pressable>
        ) : null}
        <Pressable testID="sticker-done" onPress={done} className="px-3 py-1.5 rounded-lg border border-ash-3">
          <Text className="text-xs text-onyx font-semibold">{t("LABEL_DONE")}</Text>
        </Pressable>
      </View>
      {draft ? <Text className="text-[11px] text-ash-2">{t("LABEL_STICKER_DRAG_HINT")}</Text> : null}
    </View>
  );
};

const Btn = ({ label, onPress, disabled, testID, a11y }: { label: string; onPress: () => void; disabled?: boolean; testID: string; a11y?: string }) => (
  <Pressable
    testID={testID}
    disabled={disabled}
    onPress={onPress}
    accessibilityLabel={a11y ?? label}
    className="w-8 h-8 rounded-full border border-ash-3 items-center justify-center"
  >
    <Text className={disabled ? "text-ash-2" : "text-onyx"}>{label}</Text>
  </Pressable>
);

export default StickerTray;
